"use client";

import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { Loader2, Maximize2, Minimize2, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { replaceTextareaSelection } from "@/lib/dictation/insertion";
import { activateDictationTarget, registerDictationTarget, getDictationTarget } from "@/lib/dictation/target-registry";
const COMPACT_MAX_HEIGHT_PX = 180;
function resizeCompactTextarea(textarea: HTMLTextAreaElement) {
  textarea.style.height = "auto";
  const nextHeight = Math.min(textarea.scrollHeight, COMPACT_MAX_HEIGHT_PX);
  textarea.style.height = `${nextHeight}px`;
  textarea.style.overflowY = textarea.scrollHeight > COMPACT_MAX_HEIGHT_PX ? "auto" : "hidden";
}
interface AssistantComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  isStreaming: boolean;
  isDisabled?: boolean;
  disabledLabel?: string;
  showStreamingIndicator?: boolean;
  isDictationUnavailable?: boolean;
  maxCharacters?: number;
}
export interface AssistantComposerHandle {
  focus: () => void;
  focusForDictation: () => string | null;
}
interface TextareaSnapshot {
  selectionStart: number;
  selectionEnd: number;
  selectionDirection: "forward" | "backward" | "none";
  scrollTop: number;
}
export const AssistantComposer = forwardRef<AssistantComposerHandle, AssistantComposerProps>(function AssistantComposer({
  value,
  onChange,
  onSend,
  isStreaming,
  isDisabled = false,
  disabledLabel = "Assistant unavailable",
  showStreamingIndicator = isStreaming,
  isDictationUnavailable = false,
  maxCharacters
}, forwardedRef) {
  const textareaId = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const dictationLifetimeRef = useRef(0);
  const toggleSnapshotRef = useRef<TextareaSnapshot | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [rejectedDraft, setRejectedDraft] = useState<string | null>(null);
  const hasLimitError = rejectedDraft === value;
  useEffect(() => {
    valueRef.current = value;
    onChangeRef.current = onChange;
  }, [value, onChange]);
  useImperativeHandle(forwardedRef, () => ({
    focus: () => textareaRef.current?.focus(),
    focusForDictation: () => {
      const targetId = `assistant:${textareaId}`;
      if (isDisabled || isDictationUnavailable || !getDictationTarget(targetId)) return null;
      textareaRef.current?.focus();
      return targetId;
    }
  }), [textareaId, isDisabled, isDictationUnavailable]);
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    if (isExpanded) {
      textarea.style.height = "";
      textarea.style.overflowY = "auto";
    } else {
      resizeCompactTextarea(textarea);
    }
    const snapshot = toggleSnapshotRef.current;
    if (!snapshot) return;
    textarea.focus({
      preventScroll: true
    });
    textarea.setSelectionRange(snapshot.selectionStart, snapshot.selectionEnd, snapshot.selectionDirection);
    textarea.scrollTop = snapshot.scrollTop;
    toggleSnapshotRef.current = null;
  }, [value, isExpanded]);
  useEffect(() => {
    const textarea_0 = textareaRef.current;
    if (!textarea_0 || isExpanded || typeof ResizeObserver === "undefined") {
      return;
    }
    let previousWidth = textarea_0.getBoundingClientRect().width;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      if (width === previousWidth) return;
      previousWidth = width;
      resizeCompactTextarea(textarea_0);
    });
    observer.observe(textarea_0);
    return () => observer.disconnect();
  }, [isExpanded]);
  function toggleExpanded() {
    const textarea_1 = textareaRef.current;
    if (textarea_1) {
      toggleSnapshotRef.current = {
        selectionStart: textarea_1.selectionStart,
        selectionEnd: textarea_1.selectionEnd,
        selectionDirection: textarea_1.selectionDirection,
        scrollTop: textarea_1.scrollTop
      };
    }
    setIsExpanded(current => !current);
  }
  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (isDisabled) return;
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    attemptSend();
  }
  function attemptSend() {
    if (!value.trim()) return;
    if (maxCharacters !== undefined && value.length > maxCharacters) {
      setRejectedDraft(value);
      return;
    }
    setRejectedDraft(null);
    onSend();
  }
  function handleChange(nextValue: string) {
    setRejectedDraft(null);
    onChange(nextValue);
  }
  const toggleLabel = isExpanded ? "Collapse composer" : "Expand composer";
  const lockDictationTarget = useCallback(() => {
    const textarea_2 = textareaRef.current;
    if (!textarea_2) return null;
    const originalValue = valueRef.current;
    const start = textarea_2.selectionStart;
    const end = textarea_2.selectionEnd;
    const lifetime = dictationLifetimeRef.current;
    const isCurrentTarget = () => dictationLifetimeRef.current === lifetime && textarea_2.isConnected;
    return {
      isValid() {
        return isCurrentTarget() && valueRef.current === originalValue;
      },
      insert(text: string) {
        if (!isCurrentTarget() || valueRef.current !== originalValue) {
          throw new Error("STALE_DICTATION_TARGET");
        }
        const result = replaceTextareaSelection(originalValue, start, end, text);
        onChangeRef.current(result.value);
        valueRef.current = result.value;
        requestAnimationFrame(() => {
          textarea_2.focus({
            preventScroll: true
          });
          textarea_2.setSelectionRange(result.caret, result.caret);
        });
        return () => {
          if (!isCurrentTarget() || valueRef.current !== result.value) return;
          onChangeRef.current(originalValue);
          valueRef.current = originalValue;
          requestAnimationFrame(() => {
            textarea_2.focus({
              preventScroll: true
            });
            textarea_2.setSelectionRange(start, end);
          });
        };
      },
      restoreFocus() {
        if (isCurrentTarget()) textarea_2.focus({
          preventScroll: true
        });
      }
    };
  }, []);
  useEffect(() => {
    if (isDictationUnavailable || isDisabled) return;
    dictationLifetimeRef.current += 1;
    const targetId_0 = `assistant:${textareaId}`;
    const unregister = registerDictationTarget({
      id: targetId_0,
      label: "AI assistant",
      surface: "assistant",
      isFocused: () => document.activeElement === textareaRef.current,
      lockTarget: lockDictationTarget
    });
    return () => {
      dictationLifetimeRef.current += 1;
      unregister();
    };
  }, [isDictationUnavailable, isDisabled, lockDictationTarget, textareaId]);
  return <div className="assistant-composer-shell">
            <div className={cn("assistant-input-row", isExpanded && "assistant-input-row--expanded")}>
                <textarea id={textareaId} ref={textareaRef} rows={2} placeholder="Ask the assistant…" value={value} disabled={isDisabled} aria-invalid={hasLimitError || undefined} aria-describedby={hasLimitError ? `${textareaId}-limit` : undefined} onChange={event_0 => handleChange(event_0.target.value)} onFocus={() => activateDictationTarget(`assistant:${textareaId}`)} onKeyDown={handleKeyDown} className={cn("assistant-input", isExpanded && "assistant-input--expanded")} />
                <button type="button" disabled={isDisabled} onMouseDown={event_1 => event_1.preventDefault()} onClick={toggleExpanded} className="assistant-composer-resize-btn" aria-label={toggleLabel} aria-controls={textareaId} aria-expanded={isExpanded} title={toggleLabel}>
                    {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </button>
                <button type="button" onClick={attemptSend} disabled={isDisabled || !value.trim()} className="assistant-send-btn" aria-label={isDisabled ? disabledLabel : isStreaming ? "Queue message" : "Send"}>
                    {showStreamingIndicator ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
            </div>
            {hasLimitError && maxCharacters !== undefined && <p id={`${textareaId}-limit`} className="assistant-composer-limit-error" aria-live="polite">
                    Message is over the {maxCharacters.toLocaleString()}-character limit.
                </p>}
        </div>;
});